/**
 * Initialization Gate for Ad Loading
 *
 * Ensures that ads wait for SetPageOpts to complete tag definition
 * before attempting to load. This prevents race conditions where ads
 * try to load before their tags are defined in the apntag.anq queue.
 *
 * The gate is created eagerly on module load to guarantee it always exists,
 * even if ads mount before SetPageOpts runs.
 */

// Create initialization gate eagerly on module load
let resolveInitialization: (() => void) | null = null;
let initializationPromise: Promise<void> = new Promise((resolve) => {
  resolveInitialization = resolve;
});

/**
 * Signals that initialization is complete and ads can start loading.
 * Should be called after tagAndBatch completes in SetPageOpts.
 */
export const signalInitializationComplete = () => {
  if (resolveInitialization) {
    resolveInitialization();
    resolveInitialization = null;
  }
};

/**
 * Returns a promise that resolves when initialization is complete.
 * Always returns a valid promise (created eagerly on module load).
 */
export const waitForInitialization = (): Promise<void> => {
  return initializationPromise;
};

/**
 * Resets the initialization gate for navigation or page changes.
 * Creates a new promise that ads will wait for on the next page.
 * Should be called before starting a new page initialization.
 */
export const resetInitializationGate = () => {
  // Create new promise instead of setting to null
  initializationPromise = new Promise((resolve) => {
    resolveInitialization = resolve;
  });
};
